/**
 * TreatmentAssessmentModal.tsx
 *
 * Full-screen modal showing comprehensive treatment assessment results after
 * running assessment from the influent entry page.
 *
 * Sections:
 *  1. Influent Profile Summary (key parameters + classifications)
 *  2. Recommended Treatment Train (stage cards)
 *  3. Process Compatibility (top 8 processes with score bars)
 *  4. Predicted Effluent Quality (BOD/COD/TSS reductions)
 *  5. Chemistry & Treatability (biological viability + pre-treatment needs)
 *
 * Actions:
 *  • "Generate Proposal" — creates a process train + proposal, navigates to /proposals
 *  • "View Full Analysis" — navigates to /parameters
 *  • "Close" — dismisses modal
 */

import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  X, FileText, ArrowRight, CheckCircle2, AlertTriangle,
  XCircle, Zap, Droplets, Beaker, FlaskConical,
  TrendingDown, Activity, Shield, Layers,
} from 'lucide-react';
import { useCalculator } from '@/hooks/useCalculator';
import { useProposal } from '@/context/ProposalContext';
import { toast } from 'sonner';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  sampleName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ScoreBar({ score, label }: { score: number; label: string }) {
  const color =
    score >= 80 ? 'bg-emerald-500' :
    score >= 60 ? 'bg-sky-500' :
    score >= 40 ? 'bg-amber-500' :
    'bg-rose-500';
  const textColor =
    score >= 80 ? 'text-emerald-400' :
    score >= 60 ? 'text-sky-400' :
    score >= 40 ? 'text-amber-400' :
    'text-rose-400';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-300 truncate pr-2">{label}</span>
        <span className={`text-[11px] font-black shrink-0 ${textColor}`}>{score}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function PassChip({ pass, label }: { pass: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border ${
      pass
        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
        : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
    }`}>
      {pass
        ? <CheckCircle2 className="w-3 h-3 shrink-0" />
        : <XCircle className="w-3 h-3 shrink-0" />}
      {label}
    </div>
  );
}

const STAGE_COLORS: Record<string, string> = {
  'Pre-Treatment':        'from-amber-500/20 to-amber-500/5  border-amber-500/30  text-amber-400',
  'Primary Treatment':    'from-sky-500/20   to-sky-500/5    border-sky-500/30    text-sky-400',
  'Secondary Treatment':  'from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-400',
  'Filtration':           'from-cyan-500/20  to-cyan-500/5   border-cyan-500/30   text-cyan-400',
  'Tertiary / Polishing': 'from-teal-500/20  to-teal-500/5   border-teal-500/30   text-teal-400',
  'Tertiary / Desalination':'from-blue-500/20 to-blue-500/5  border-blue-500/30   text-blue-400',
  'Polishing':            'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400',
};

function stageColor(stage: string) {
  for (const [key, val] of Object.entries(STAGE_COLORS)) {
    if (stage.startsWith(key.split(' ')[0])) return val;
  }
  return 'from-slate-500/20 to-slate-500/5 border-slate-600/30 text-slate-400';
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function TreatmentAssessmentModal({ open, onClose, sampleName }: Props) {
  const navigate = useNavigate();
  const { result, params } = useCalculator();
  const { saveProposal } = useProposal();
  const simulation = result.simulation;

  // Top 8 processes by compatibility score
  const topProcesses = useMemo(
    () => [...(result.processCompatibility ?? [])].sort((a, b) => b.score - a.score).slice(0, 8),
    [result.processCompatibility]
  );

  const bioViability = result.biologicalViability ?? [];
  const trainStages  = result.treatmentTrain ?? [];
  const preTreat     = result.preTreatmentRequirements ?? [];

  const bod5In  = params.bod5;
  const codIn   = params.cod;
  const tssIn   = params.tss;
  const bod5Out = simulation ? Math.round(bod5In * (1 - (simulation.totalBodReduction ?? 0))) : null;
  const codOut  = simulation ? Math.round(codIn  * (1 - (simulation.totalCodReduction ?? 0))) : null;
  const tssOut  = simulation ? Math.round(tssIn  * (1 - (simulation.totalTssReduction ?? 0))) : null;

  const bioPass  = bioViability.filter(b => b.passFail === 'PASS').length;
  const bioTotal = bioViability.length;

  // ─── Generate Proposal ────────────────────────────────────────────────────

  const handleGenerateProposal = () => {
    try {
      const yr  = new Date().getFullYear();
      const seq = String(Math.floor(Math.random() * 9000) + 1000).padStart(5, '0');
      const proposalNumber = `PRO-${yr}-${seq}-R0`;
      const baseNumber     = `PRO-${yr}-${seq}`;
      saveProposal({
        proposalNumber,
        baseNumber,
        revisionNumber: 0,
        title: `Treatment Assessment — ${sampleName}`,
        description: `Auto-generated from influent sample "${sampleName}".`,
        notes: `Recommended treatment train: ${trainStages.map(s => s.process).join(' → ')}.\nTreatability: ${result.treatabilityClassification || 'N/A'}. Recommended approach: ${result.recommendedApproach || 'N/A'}.`,
        customerId: null,
        customerName: null,
        status: 'draft',
        isLocalOnly: true,
        isImmutable: false,
        savedToDb: false,
        syncStatus: 'pending',
        sampleIds: [],
      });
      toast.success(`Proposal ${proposalNumber} created`);
      onClose();
      navigate('/proposals');
    } catch {
      toast.error('Failed to create proposal');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="assessment-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-stretch justify-center bg-black/75 backdrop-blur-sm overflow-hidden"
        >
          <motion.div
            key="assessment-modal-panel"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative w-full max-w-5xl m-4 rounded-2xl bg-[#0b1628] border border-slate-800/60 overflow-hidden flex flex-col"
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/50 bg-[#0a1220] shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-sky-500/10 border border-sky-500/25 flex items-center justify-center">
                  <FlaskConical className="w-4.5 h-4.5 text-sky-400" />
                </div>
                <div>
                  <h2 className="text-base font-black text-slate-100">Treatment Assessment</h2>
                  <p className="text-[11px] text-slate-500 truncate max-w-xs">{sampleName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { onClose(); navigate('/parameters'); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-400 border border-slate-700 hover:border-sky-500/40 hover:text-sky-400 transition-all"
                >
                  View Full Analysis <ArrowRight className="w-3 h-3" />
                </button>
                <button
                  onClick={handleGenerateProposal}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-black bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all"
                >
                  <FileText className="w-3 h-3" /> Generate Proposal
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── Scrollable Body ── */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">

              {/* ════════════════════════════════════════════ */}
              {/* 1. SUMMARY ROW                              */}
              {/* ════════════════════════════════════════════ */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Treatability', value: result.treatabilityClassification || '—', icon: Activity, color: 'sky' },
                  { label: 'TI Score', value: result.overallTreatabilityIndex ? String(result.overallTreatabilityIndex) : '—', icon: Layers, color: 'violet' },
                  { label: 'Bio Viability', value: `${bioPass}/${bioTotal} passed`, icon: Beaker, color: bioPass >= 5 ? 'emerald' : 'rose' },
                  { label: 'Train Stages', value: String(trainStages.length), icon: Zap, color: 'amber' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className={`p-3 rounded-xl bg-${color}-500/5 border border-${color}-500/20 flex items-center gap-3`}>
                    <Icon className={`w-5 h-5 text-${color}-400 shrink-0`} />
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase font-bold">{label}</p>
                      <p className={`text-sm font-black text-${color}-400`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ════════════════════════════════════════════ */}
              {/* 2. TREATMENT TRAIN                          */}
              {/* ════════════════════════════════════════════ */}
              <div>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Zap className="w-3 h-3 text-rb-brand" /> Recommended Treatment Train
                </h3>
                {trainStages.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No treatment stages computed — enter more parameters.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {trainStages.map((stage, idx) => {
                      const col = stageColor(stage.stage);
                      return (
                        <div key={idx} className={`p-3.5 rounded-xl border bg-gradient-to-b ${col} relative`}>
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <span className="text-[10px] font-black uppercase tracking-wider opacity-70">{stage.stage}</span>
                            <span className="text-[10px] font-bold opacity-60 shrink-0">#{idx + 1}</span>
                          </div>
                          <p className="text-sm font-black text-white leading-tight">{stage.process}</p>
                          {stage.purpose && (
                            <p className="text-[10px] text-slate-400 mt-1 leading-snug">{stage.purpose}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ════════════════════════════════════════════ */}
              {/* 3. PROCESS COMPATIBILITY + EFFLUENT QUALITY */}
              {/* ════════════════════════════════════════════ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Process Compatibility */}
                <div>
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" /> Process Compatibility (Top 8)
                  </h3>
                  <div className="space-y-2.5">
                    {topProcesses.map((proc) => (
                      <div key={proc.process}>
                        <ScoreBar score={proc.score} label={proc.process} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Predicted Effluent + Bio */}
                <div className="space-y-4">
                  {/* Predicted Effluent */}
                  <div>
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <TrendingDown className="w-3 h-3 text-sky-400" /> Predicted Effluent Quality
                    </h3>
                    {simulation ? (
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'BOD₅', inf: bod5In, eff: bod5Out, unit: 'mg/L', reduction: simulation.totalBodReduction },
                          { label: 'COD',  inf: codIn,  eff: codOut,  unit: 'mg/L', reduction: simulation.totalCodReduction },
                          { label: 'TSS',  inf: tssIn,  eff: tssOut,  unit: 'mg/L', reduction: simulation.totalTssReduction },
                        ].map(({ label, inf, eff, unit, reduction }) => (
                          <div key={label} className="p-3 rounded-xl bg-slate-800/40 border border-slate-800/50 text-center">
                            <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">{label}</p>
                            <p className="text-lg font-black text-white">{eff ?? '—'}</p>
                            <p className="text-[10px] text-slate-500">{unit}</p>
                            <div className="mt-1.5 text-[10px] text-emerald-400 font-bold">
                              {reduction != null ? `↓ ${Math.round(reduction * 100)}%` : '—'}
                            </div>
                            <p className="text-[10px] text-slate-600 mt-0.5">from {inf} {unit}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-800/40 text-center">
                        <p className="text-xs text-slate-500">Run assessment to see effluent predictions</p>
                      </div>
                    )}
                  </div>

                  {/* Bio Viability */}
                  <div>
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <Shield className="w-3 h-3 text-violet-400" /> Biological Viability
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {bioViability.map((b) => (
                        <PassChip key={b.factor} pass={b.passFail === 'PASS'} label={b.factor} />
                      ))}
                    </div>
                    {result.recommendedApproach && (
                      <div className="mt-2 px-3 py-2 rounded-lg bg-sky-500/5 border border-sky-500/15 text-[10px] text-sky-400 font-bold">
                        {result.recommendedApproach}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ════════════════════════════════════════════ */}
              {/* 4. PRE-TREATMENT REQUIREMENTS               */}
              {/* ════════════════════════════════════════════ */}
              {preTreat.filter(p => p.status === 'REQUIRED').length > 0 && (
                <div>
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-amber-400" /> Pre-Treatment Required
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {preTreat
                      .filter(p => p.status === 'REQUIRED')
                      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
                      .map((req) => (
                        <div key={req.step} className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[11px] font-bold text-amber-300">{req.step}</p>
                            <p className="text-[10px] text-slate-500">{req.trigger}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* ════════════════════════════════════════════ */}
              {/* 5. INFLUENT PARAMETER CLASSIFICATION        */}
              {/* ════════════════════════════════════════════ */}
              <div>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Droplets className="w-3 h-3 text-sky-400" /> Influent Parameter Classification
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {(result.parameterClassification ?? []).map((p) => {
                    const levelColor =
                      p.level === 'Optimal' || p.level === 'Low' || p.level === 'Safe'
                        ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5'
                        : p.level === 'Moderate'
                        ? 'text-amber-400 border-amber-500/20 bg-amber-500/5'
                        : 'text-rose-400 border-rose-500/20 bg-rose-500/5';
                    return (
                      <div key={p.parameter} className={`p-2.5 rounded-lg border ${levelColor}`}>
                        <p className="text-[10px] uppercase font-bold opacity-70 truncate">{p.parameter}</p>
                        <p className="text-sm font-black mt-0.5">{p.value}</p>
                        <p className="text-[10px] opacity-80 leading-tight mt-0.5 truncate">{p.classification}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* ── Footer ── */}
            <div className="shrink-0 flex items-center justify-between px-6 py-3 border-t border-slate-800/50 bg-[#0a1220]">
              <p className="text-[10px] text-slate-600">
                Assessment based on {Object.keys(params).filter(k => (params as any)[k] > 0).length} entered parameters
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-700 transition-all"
                >
                  Close
                </button>
                <button
                  onClick={handleGenerateProposal}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-black bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all"
                >
                  <FileText className="w-3 h-3" /> Generate Proposal
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
